/**
 * Created by tdunham on 7/7/2017.
 */


import {HttpClient, HttpHeaders}            from "@angular/common/http";
import {Injectable}                         from "@angular/core";
import {isInteger}                          from "@ng-bootstrap/ng-bootstrap/util/util";

import {AngularQuery}                       from "../query/angularquery";
import {Player}                             from './player';

import {map}                                from "rxjs/operators";
import {Observable}                         from "rxjs";
import {News} from '../news/news';


@Injectable()
export class PlayerService {

	/**
	 * Initialize the object.
	 *
	 * @param http {HttpClient} Connection to HTTP.
	 */
	constructor(protected http: HttpClient) {}


	/**
	 * Merge the elements of the search together into an object and submit
	 * it to the search() method.
	 * The user types in the name as last name first, then a comma, then
	 * the first name.  Depending on what value we get for name, that determines
	 * how the search is set up.
	 *
	 * @param name       The name of the player, last name first, perhaps with a comma.
	 * @param code       The league code ('NFL', etc.).
	 * @param team       The team ID number.
	 * @returns {Observable<Player[]>}  The players found by the search.
	 */
	createSearch(name:string, code:string, team:number): Observable<Player[]> {

		// Quit right away if there aren't enough characters.
		if (2 >= name.length) {

			return Observable.of([]);

		}

		// Build an object with all the parameters needed for the search and pass it to the search function.
		let searcher: Object = {};
		searcher['code'] = code;

		if (isInteger(team)) {

			searcher['team'] = team;

		} else {

			searcher['team'] = -1;

		}

		// Parse the name string into firstname and surname values.
		let comma:number = name.indexOf(',');

		if (-1 === comma) {

			searcher['surname'] = name + '%';
			searcher['firstname'] = '';

		} else if (',' === name.substr(-1, 1)) {

			searcher['surname'] = name.substring(0, comma);
			searcher['firstname'] = '';

		} else {

			searcher['surname'] = name.substring(0, comma);
			searcher['firstname'] = name.substring(comma + 1).trim() + '%';

		}

		return this.search(searcher);

	}


	/**
	 * Delete the selected player.
	 *
	 * @param {number}  id  The player ID to delete.
	 * @return {Observable<Boolean>}  Success or failure of the delete.
	 */
	deletePlayerById(id: number): Observable<Boolean> {

		let query:AngularQuery = new AngularQuery();
		query.setEndpoint(AngularQuery.ENDPOINT_PLAYER, id.toString());
		let url:string = query.getUrl();

		return this.http
			.delete<Boolean>(url)
			.pipe(
				map(() => true)
			);

	}


	/**
	 * Get the Player objects specified by the provided Angular ReST API ID.
	 *
	 * @param id       An Angular ReST API ID value from the "article" table.
	 * @returns {Observable<Array<Player>>}  An Observable object or error.
	 */
	getPlayersByArticleId(id: number): Observable<Array<Player>> {

		let query:AngularQuery = new AngularQuery();
		query.setEndpoint(AngularQuery.ENDPOINT_NEWS_PLAYER, id.toString());
		let url:string = query.getUrl();

		return this.http
			.get<Array<Player>>(url);

	}


	/**
	 * Get a particular player by their ID number.
	 *
	 * @param {number}  id  The player ID to retrieve.
	 * @returns {Observable<Player>} The Observable which will have player data.
	 */
	getPlayerById(id: number): Observable<Player> {

		// Pass back an empty player object if the ID passed is -1.
		if (-1 === id) {

			let player: Player = new Player({'id': -1, 'firstname': '', 'surname': '',
				'position': 0, 'team': -1, 'code': 'NFL'});

			return Observable.of(player).delay(500);  // Without the delay, this won't work in some circumstances.

		}

		let query:AngularQuery = new AngularQuery();
		query.setEndpoint(AngularQuery.ENDPOINT_PLAYER, id.toString());
		let url:string = query.getUrl();

		return this.http
			.get<Player>(url);

	}


	/**
	 * This is used in the "inactives" page to retrieve a given
	 * NFL team's players.
	 *
	 * @param id                         The ID number of the NFL Team entry.
	 * @returns {Observable<Player[]>}   An Observable array of Player objects.
	 */
	getNflPlayers(id: number): Observable<Player[]> {

		let query: AngularQuery = new AngularQuery();
		query.setEndpoint(AngularQuery.ENDPOINT_PLAYER_SEARCH);
		query.setPerPage(100);
		query.addParameter('code', 'nfl');
		query.addParameter('team', id.toString());
		query.addParameter('order_field', 'firstname');
		query.addParameter('sort_order', 'asc');
		query.addParameter('nfl_inactives_list', '1');

		let url:string = query.getUrl();

		return this.http
			.get<Player[]>(url);

	}

  getRosterTeam(id: number, offset: number, itemPerPage:number): Observable<Player[]> {

    let query: AngularQuery = new AngularQuery();
    query.setEndpoint(AngularQuery.ENDPOINT_PLAYER_SEARCH);
    query.setPerPageExtended(itemPerPage);
    query.setPage(offset)
    query.addParameter('code', 'nfl');
    query.addParameter('team', id.toString());
    query.addParameter('order_field', 'surname');
    query.addParameter('sort_order', 'asc');
    query.addParameter('nfl_inactives_list', '1');

    let url: string = query.getUrl();

    return this.http
      .get<Player[]>(url);

  }


	/**
	 * Save the provided player information.
	 *
	 * @param {string} url    The URL to post to.
	 * @param {string} json   The player data as a JSON string.
	 * @return {Observable<Player>}  The saved Player data echoed back.
	 */
	save(url: string, json: string): Observable<Player> {

		// Warn them that JSON is coming.
		let headers = new HttpHeaders().append('Content-Type', 'application/json');
		return this.http
			.post<Player>(url, json, { headers: headers });

	}


	/**
	 * Search for the player using the parameters supplied in the object and return an Observable.
	 *
	 * @param {Object} parameters  An Object with the search parameters.
	 * @returns {Observable<Player[]>}  An Observable with Players found.
	 */
	search(parameters: Object): Observable<Player[]> {

		// There must be at least 2 characters to start a real search.
		if (2 > parameters['surname'].length && 2 > parameters['firstname'].length) {

			return Observable.of([]);

		}

		let query: AngularQuery = new AngularQuery();
		query.setEndpoint(AngularQuery.ENDPOINT_PLAYER_SEARCH);     // There is a special player search table.
		query.setPerPage(8);                                        // Limit results to 8 entries.

		// Any or all of the following arguments may be used.
		if (0 !== parameters['firstname'].length) {

			query.addParameter('firstname', parameters['firstname']);

		}

		if (0 !== parameters['surname'].length) {

			query.addParameter('surname', parameters['surname']);

		}

		if (0 !== parameters['code'].length) {

			query.addParameter('code', parameters['code']);

		}

		if (-1 !== parameters['team']) {

			query.addParameter('team', parameters['team'].toString());

		}

		let url:string = query.getUrl();

		return this.http
			.get<Player[]>(url);

	}
  massRosterUpdate(url: string, json: string): Observable<Player> {

    // Warn them that JSON is coming.
    let headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.http
      .post<any>(url, json, { headers: headers });

  }
}



// WEBPACK FOOTER //
// ./src/app/services/player/player.service.ts